import { useEffect, useRef, useState } from 'react';
import { startVoiceRecording, VoiceError, type VoiceRecorder } from '@/lib/voice';
import { useHaptic } from '@/app/hooks/useHaptic';

/**
 * Запись голосового ответа для поля ввода.
 *
 * Сам рекордер живёт в ref: перерисовка экрана не должна обрывать запись.
 * Наружу отдаётся только то, что рисуется, — идёт ли запись, уровень сигнала,
 * длительность и причина отказа.
 */

/** Случайное касание даёт запись в доли секунды: отправлять её нечего. */
const MIN_DURATION_MS = 700;

export interface VoiceRecording {
  recording: boolean;
  level: number;
  elapsedMs: number;
  /** Причина, по которой запись не началась или не годится. */
  error: string | null;
  start(): Promise<void>;
  /** Останавливает запись и отдаёт файл; null — записывать было нечего. */
  stop(): Promise<Blob | null>;
  cancel(): void;
  lastDurationMs: number;
}

export function useVoiceRecorder(): VoiceRecording {
  const haptic = useHaptic();
  const recorderRef = useRef<VoiceRecorder | null>(null);
  const startedAtRef = useRef(0);
  const durationRef = useRef(0);

  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Уровень сигнала читается из рекордера, который живёт вне состояния React,
  // а длительность считается от отметки старта: накопление по тикам таймера
  // расходится с настоящим временем записи
  useEffect(() => {
    if (!recording) return undefined;

    const timer = window.setInterval(() => {
      const recorder = recorderRef.current;
      if (!recorder) return;
      setLevel(recorder.level);
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 100);
    return () => window.clearInterval(timer);
  }, [recording]);

  // Уход с экрана обязан освободить микрофон: иначе индикатор записи остаётся
  // гореть у пользователя после закрытия диалога
  useEffect(
    () => () => {
      recorderRef.current?.cancel();
      recorderRef.current = null;
    },
    []
  );

  const release = () => {
    recorderRef.current = null;
    setRecording(false);
    setLevel(0);
    setElapsedMs(0);
  };

  return {
    recording,
    level,
    elapsedMs,
    error,
    get lastDurationMs() {
      return durationRef.current;
    },

    async start() {
      setError(null);
      try {
        recorderRef.current = await startVoiceRecording();
        startedAtRef.current = Date.now();
        setLevel(0);
        setElapsedMs(0);
        setRecording(true);
        haptic.impact('light');
      } catch (cause) {
        // Причина важнее самого отказа: запрет в настройках, отсутствие
        // микрофона и клиент без записи требуют разных действий
        setError(
          cause instanceof VoiceError
            ? cause.message
            : 'Микрофон недоступен. Наберите ответ текстом'
        );
      }
    },

    async stop() {
      const recorder = recorderRef.current;
      if (!recorder) return null;
      release();

      const { blob, durationMs } = await recorder.stop();
      durationRef.current = durationMs;

      if (durationMs < MIN_DURATION_MS) {
        setError('Слишком короткая запись — удерживайте запись хотя бы секунду');
        return null;
      }
      return blob;
    },

    cancel() {
      recorderRef.current?.cancel();
      release();
    },
  };
}
